import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faAnglesLeft,
    faAnglesRight,
} from "@fortawesome/free-solid-svg-icons";

interface Props {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

type PageEntry = number | "ellipsis";

function buildPageWindow(current: number, total: number): PageEntry[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const candidates = new Set<number>([1, total, current - 1, current, current + 1]);
    if (current <= 3) [2, 3, 4, 5].forEach((p) => candidates.add(p));
    if (current >= total - 2)
        [total - 1, total - 2, total - 3, total - 4].forEach((p) => candidates.add(p));

    const sorted = [...candidates].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const result: PageEntry[] = [];
    sorted.forEach((p, i) => {
        if (i > 0 && p - sorted[i - 1] > 1) result.push("ellipsis");
        result.push(p);
    });
    return result;
}

const navBtnClass =
    "flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-ink-muted transition-colors enabled:hover:bg-surface-3 enabled:hover:text-ink focus-visible:ring-2 focus-visible:ring-accent/40 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40";
const pageBtnBase =
    "flex h-9 min-w-9 cursor-pointer items-center justify-center rounded-full px-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus:outline-none";

export default function Pagination({ currentPage, totalPages, onPageChange }: Props) {
    const total = Math.max(1, totalPages || 1);
    const [pageInput, setPageInput] = useState<number | string>(currentPage);

    useEffect(() => {
        setPageInput(currentPage);
    }, [currentPage]);

    const commit = () => {
        if (pageInput === "" || pageInput == null) {
            setPageInput(currentPage);
            return;
        }
        const n = Math.max(1, Math.min(Number(pageInput) || 1, total));
        if (n !== currentPage) onPageChange(n);
        else setPageInput(currentPage);
    };

    const go = (page: number) => {
        const next = Math.max(1, Math.min(page, total));
        if (next !== currentPage) onPageChange(next);
    };

    const atStart = currentPage <= 1;
    const atEnd = currentPage >= total;
    const pages = buildPageWindow(currentPage, total);

    return (
        <nav className="flex items-center gap-1 text-sm text-ink" aria-label="pagination">
            <button
                type="button"
                onClick={() => go(1)}
                disabled={atStart}
                aria-label="first page"
                className={navBtnClass}
            >
                <FontAwesomeIcon icon={faAnglesLeft} className="text-xs" />
            </button>
            <button
                type="button"
                onClick={() => go(currentPage - 1)}
                disabled={atStart}
                aria-label="previous page"
                className={navBtnClass}
            >
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
            </button>

            <div className="flex items-center gap-1 px-1 @md:hidden">
                <input
                    type="text"
                    inputMode="numeric"
                    value={pageInput}
                    disabled={total === 1}
                    onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) setPageInput(e.target.value);
                    }}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    }}
                    aria-label={`page ${currentPage} of ${total}`}
                    className="w-12 rounded-md border border-border bg-surface px-2 py-1.5 text-center text-sm text-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/40 disabled:opacity-50"
                />
                <span className="text-ink-muted">/ {total}</span>
            </div>

            <ol className="hidden items-center gap-1 @md:flex" aria-label="pages">
                {pages.map((entry, ix) =>
                    entry === "ellipsis" ? (
                        <li
                            key={`ellipsis-${ix}`}
                            className="flex h-9 w-6 items-center justify-center text-ink-muted"
                            aria-hidden
                        >
                            …
                        </li>
                    ) : (
                        <li key={entry}>
                            <button
                                type="button"
                                onClick={() => go(entry)}
                                aria-current={entry === currentPage ? "page" : undefined}
                                aria-label={`page ${entry}`}
                                className={[
                                    pageBtnBase,
                                    entry === currentPage
                                        ? "bg-accent text-white"
                                        : "text-ink-muted hover:bg-surface-3 hover:text-ink",
                                ].join(" ")}
                            >
                                {entry}
                            </button>
                        </li>
                    ),
                )}
            </ol>

            <button
                type="button"
                onClick={() => go(currentPage + 1)}
                disabled={atEnd}
                aria-label="next page"
                className={navBtnClass}
            >
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
            </button>
            <button
                type="button"
                onClick={() => go(total)}
                disabled={atEnd}
                aria-label="last page"
                className={navBtnClass}
            >
                <FontAwesomeIcon icon={faAnglesRight} className="text-xs" />
            </button>
        </nav>
    );
}
